// Reusable "Sync" button + status pill.
//
// Any page in the app can drop this in to trigger a mirror-refresh
// (`POST /api/sync/{resource}`) and show the last-synced timestamp
// (`GET /api/sync/status`).  Result is stored in `sync_log` so every
// user sees the same "Last synced: 5 min ago" state — not a per-tab
// cache.
//
// Usage:
//   <SyncButton resource="orders" />           // full-collection refresh
//   <SyncButton resource="person" targetId={personId} label="Sync this customer" />
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60)    return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)    return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48)    return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function SyncButton({
  resource,             // "persons" | "orders" | "calls" | "quotes" | "person" | "order"
  targetId = null,      // for single-record syncs
  label,
  variant = "default",  // default | subtle | icon
  className = "",
}) {
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("talmia_token") || "";

  const loadStatus = () => {
    axios.get(`${API}/sync/status?resource=${resource}`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then((r) => setLatest(r.data.latest?.[resource] || null))
      .catch(() => setLatest(null));
  };
  useEffect(() => { loadStatus(); }, [resource]);

  const trigger = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (targetId && resource === "person") params.set("person_id", targetId);
      if (targetId && resource === "order")  params.set("order_id",  targetId);
      const url = `${API}/sync/${resource}${params.toString() ? "?" + params.toString() : ""}`;
      const r = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success("Sync queued", {
        description: r.data.note || "Recorded to sync log",
      });
      loadStatus();
    } catch (e) {
      toast.error("Sync failed", { description: e?.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  const status = latest?.status;
  const statusIcon = status === "ok"
    ? <CheckCircle2 className="w-3 h-3 text-emerald-300" />
    : status === "error"
    ? <AlertCircle className="w-3 h-3 text-rose-300" />
    : null;

  const displayLabel = label || (targetId ? "Sync this record" : `Sync ${resource}`);

  if (variant === "icon") {
    return (
      <button onClick={trigger} disabled={busy}
        title={`${displayLabel} · Last: ${fmtRelative(latest?.finished_at)}`}
        className={`p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white ${className}`}
        data-testid={`sync-btn-${resource}`}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
      </button>
    );
  }

  const chip = (
    <button
      onClick={trigger}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${
        variant === "subtle"
          ? "border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-slate-300"
          : "border-teal-500/40 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20"
      } font-mono uppercase tracking-widest text-[10.5px] ${className}`}
      data-testid={`sync-btn-${resource}`}
      title={latest ? `Last sync ${latest.status} by ${latest.by || "system"}` : "No sync yet"}
    >
      <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
      {busy ? "Syncing…" : displayLabel}
      {statusIcon}
      <span className="normal-case tracking-normal text-slate-400 ml-0.5">
        · {fmtRelative(latest?.finished_at)}
      </span>
    </button>
  );
  return chip;
}
